// [PURPOSE]: Register proficiencyLevel:2 (expertise) effects from
//   playerStats.expertiseSkills. Players accumulate expertise picks via
//   class features (Rogue Expertise L1+L6, Bard Expertise L3+L10, Cleric
//   Knowledge Blessings of Knowledge L1) and certain feats (Skill Expert,
//   Prodigy). The wizard saves picks to playerStats.expertiseSkills (an
//   array of skill keys); this hook registers the matching proficiency
//   effects every time the array changes.
// [WHY]: Bucket 2/3 follow-up. The calculator/registry already honor
//   proficiencyLevel:2 (verified in calculatorRegistryGaps.test.js); the
//   missing piece was a producer that registers effects from the wizard's
//   stored picks. Mirrors useFeatEffects (which reads feat.customChoices)
//   and useClassFeatureEffects (which reads classes[i] features).
// [INTEGRATION]: Mount once in App.jsx. Watches playerStats.expertiseSkills.
//   Cleanup via removeByPrefix('expertise:').

import { useEffect } from 'react';
import { effectRegistry } from '../services/effectRegistry';
import { effectCalculator } from '../services/effectCalculator';

export function useExpertiseEffects(playerStats) {
  const expertiseSkills = playerStats?.expertiseSkills;

  useEffect(() => {
    // [CLEANUP]: Clear any prior expertise registrations so picks are reset
    // cleanly when the wizard updates the list.
    effectRegistry.removeByPrefix('expertise:');

    if (!Array.isArray(expertiseSkills) || expertiseSkills.length === 0) {
      effectCalculator.clearCache();
      return;
    }

    // De-dupe in case the wizard wrote the same skill twice (multiclass
    // Bard + Rogue might both grant expertise in the same skill — RAW
    // doesn't stack beyond level 2, so de-dupe is safe).
    const uniqueSkills = Array.from(new Set(expertiseSkills));

    uniqueSkills.forEach(skill => {
      if (!skill || typeof skill !== 'string') return;
      try {
        // [WHY]: Use the skill key as-is. Convention is camelCase for multi-word
        // skills (sleightOfHand, animalHandling) matching SkillsPanel state keys.
        // The wizard step writes those camelCase keys; calculateSkillBonus is
        // called with matching keys; getGrantedProficiencyLevel builds
        // 'skill:<key>'. No case normalization — the contract is that the
        // wizard writes valid keys.
        effectRegistry.register(
          `expertise:${skill}`,
          [{ type: 'proficiency', operation: 'grant', value: `skill:${skill}`, proficiencyLevel: 2 }],
          playerStats
        );
      } catch (err) {
        console.warn(`[useExpertiseEffects] failed to register expertise for ${skill}`, err);
      }
    });

    effectCalculator.clearCache();

    return () => {
      effectRegistry.removeByPrefix('expertise:');
      effectCalculator.clearCache();
    };
  }, [expertiseSkills, playerStats]);
}

export default useExpertiseEffects;
